import React from 'react';

import { cn } from '@/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'emerald' | 'amber' | 'navy' | 'purple';
  text?: string;
  className?: string;
}

/**
 * Accessible loading spinner with Islamic design elements
 * Supports multiple sizes and colors with proper ARIA attributes
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'emerald',
  text,
  className,
}) => {
  // Size configurations
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
  };

  // Color configurations
  const colorClasses = {
    emerald: {
      primary: 'border-emerald-600',
      secondary: 'border-emerald-200',
      accent: 'border-emerald-300',
    },
    amber: {
      primary: 'border-amber-600',
      secondary: 'border-amber-200',
      accent: 'border-amber-300',
    },
    navy: {
      primary: 'border-navy-600',
      secondary: 'border-navy-200',
      accent: 'border-navy-300',
    },
    purple: {
      primary: 'border-purple-600',
      secondary: 'border-purple-200',
      accent: 'border-purple-300',
    },
  };

  // Text size based on spinner size
  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
  };

  const colorConfig = colorClasses[color];
  const loadingText = text || 'Loading...';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center',
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={loadingText}
    >
      {/* Islamic-inspired loading spinner */}
      <div className="relative">
        {/* Outer ring */}
        <div
          className={cn(
            sizeClasses[size],
            'rounded-full border-4 border-transparent animate-spin',
            colorConfig.primary,
            'border-t-current'
          )}
          style={{
            animation: 'spin 1s linear infinite',
          }}
        />
        
        {/* Inner ring (counter-rotating) */}
        <div
          className={cn(
            sizeClasses[size],
            'absolute inset-0 rounded-full border-4 border-transparent animate-spin',
            colorConfig.accent,
            'border-r-current'
          )}
          style={{
            animation: 'spin 1.5s linear infinite reverse',
            animationDelay: '-0.15s',
          }}
        />
        
        {/* Center dot for larger spinners */}
        {(size === 'lg' || size === 'xl') && (
          <div
            className={cn(
              'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
              'w-1 h-1 rounded-full',
              colorConfig.primary.replace('border-', 'bg-')
            )}
          />
        )}
      </div>

      {/* Loading text */}
      {text && (
        <p
          className={cn(
            'mt-3 text-gray-600 text-center font-medium',
            textSizeClasses[size]
          )}
        >
          {text}
        </p>
      )}

      {/* Screen reader text */}
      <span className="sr-only">
        {loadingText}
      </span>
    </div>
  );
};

// Predefined loading spinner variants
export const LoadingSpinnerVariants = {
  Dashboard: () => (
    <LoadingSpinner 
      size="lg" 
      color="emerald" 
      text="Loading dashboard..." 
    />
  ),
  
  AdminRequests: () => (
    <LoadingSpinner 
      size="md" 
      color="amber" 
      text="Loading admin requests..." 
    />
  ),
  
  Authentication: () => (
    <LoadingSpinner 
      size="lg" 
      color="emerald" 
      text="Authenticating..." 
    />
  ),
  
  Processing: () => (
    <LoadingSpinner 
      size="md" 
      color="navy" 
      text="Processing request..." 
    />
  ),
  
  Small: () => (
    <LoadingSpinner 
      size="sm" 
      color="emerald" 
    />
  ),
};

// Loading skeleton component for content placeholders
interface LoadingSkeletonProps {
  className?: string;
  lines?: number;
  showAvatar?: boolean;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  className,
  lines = 3,
  showAvatar = false,
}) => {
  return (
    <div className={cn('animate-pulse', className)} role="status" aria-label="Loading content">
      {/* Avatar skeleton */}
      {showAvatar && (
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full mr-3" />
          <div className="space-y-2">
            <div className="w-24 h-4 bg-gray-200 rounded" />
            <div className="w-16 h-3 bg-gray-200 rounded" />
          </div>
        </div>
      )}
      
      {/* Content lines */}
      <div className="space-y-2">
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className={cn(
              'bg-gray-200 rounded h-4',
              index === lines - 1 ? 'w-3/4' : 'w-full'
            )}
          />
        ))}
      </div>
      
      <span className="sr-only">Loading content...</span>
    </div>
  );
};

// Full page loading component
interface FullPageLoadingProps {
  title?: string;
  subtitle?: string;
  showProgress?: boolean;
  progress?: number;
}

export const FullPageLoading: React.FC<FullPageLoadingProps> = ({
  title = 'muadhin Operations Dashboard',
  subtitle = 'Loading...',
  showProgress = false,
  progress = 0,
}) => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md w-full px-4">
        {/* Islamic pattern background */}
        <div className="islamic-pattern absolute inset-0 opacity-50" />
        
        {/* Content */}
        <div className="relative">
          <LoadingSpinner size="xl" color="emerald" />
          
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            {title}
          </h1>
          
          <p className="mt-2 text-gray-600">
            {subtitle}
          </p>
          
          {/* Progress bar */}
          {showProgress && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {Math.round(progress)}% complete
              </p>
            </div>
          )}
          
          {/* Islamic greeting */}
          <div className="mt-8 text-sm text-emerald-600">
            <p dir="rtl" className="font-arabic text-lg">
              بسم الله الرحمن الرحيم
            </p>
            <p className="mt-1">
              In the name of Allah, the Most Gracious, the Most Merciful
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
